import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

const VoiceNotePlayer = ({ audioUrl, duration, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const animationRef = useRef(null);

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar || !audio.duration || !isFinite(audio.duration)) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, clickX / width)); // Clamp between 0 and 1
    const newTime = percentage * audio.duration;
    
    if (isFinite(newTime) && newTime >= 0) {
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (!isFinite(time) || isNaN(time) || time < 0) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!audioRef.current || !audioRef.current.duration || !isFinite(audioRef.current.duration)) return 0;
    return Math.max(0, Math.min(100, (currentTime / audioRef.current.duration) * 100));
  };

  const generateWaveformBars = () => {
    const bars = [];
    const barCount = 15; // Reduced for compact display
    const progress = getProgressPercentage();
    
    for (let i = 0; i < barCount; i++) {
      const barProgress = (i / barCount) * 100;
      const isActive = barProgress <= progress;
      const height = Math.random() * 8 + 4; // Random height between 4-12px
      
      bars.push(
        <div
          key={i}
          className={`w-0.5 rounded-full transition-all duration-150 ${
            isActive 
              ? 'bg-blue-500 animate-pulse' 
              : 'bg-gray-300'
          }`}
          style={{ 
            height: `${height}px`,
            animationDelay: `${i * 0.05}s`
          }}
        />
      );
    }
    
    return bars;
  };

  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-2 ${className}`}>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        className="hidden"
      />
      
      <div className="flex items-center space-x-2">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          disabled={isLoading}
          className="p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-3 w-3" />
          ) : (
            <Play className="h-3 w-3 ml-0.5" />
          )}
        </button>

        {/* Waveform Visualization */}
        <div className="flex-1 min-w-0">
          <div
            ref={progressRef}
            onClick={handleProgressClick}
            className="flex items-center space-x-0.5 cursor-pointer py-1"
          >
            {generateWaveformBars()}
          </div>
        </div>

        {/* Time Display */}
        <div className="text-xs text-gray-500 font-mono min-w-[30px]">
          {formatTime(currentTime)}
        </div>

        {/* Speed Control */}
        <select
          value={playbackRate}
          onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
          className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white min-w-[35px]"
        >
          {speedOptions.map(speed => (
            <option key={speed} value={speed}>
              {speed}x
            </option>
          ))}
        </select>

        {/* Mute Button */}
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
        >
          {isMuted ? (
            <VolumeX className="h-3 w-3" />
          ) : (
            <Volume2 className="h-3 w-3" />
          )}
        </button>
      </div>
    </div>
  );
};

export default VoiceNotePlayer;
